import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import NoSleep from 'nosleep.js';

interface NoSleepContextProps {
  enableNoSleep: () => void;
  disableNoSleep: () => void;
  isNoSleepEnabled: boolean;
}

const NoSleepContext = createContext<NoSleepContextProps | undefined>(undefined);

export function NoSleepProvider({ children }: { children: React.ReactNode }) {
  const [isNoSleepEnabled, setIsNoSleepEnabled] = useState(false);
  const noSleep = useMemo(() => new NoSleep(), []);

  /**
   * Keeping the screen awake is a convenience, never a requirement, so failures are
   * swallowed deliberately.
   *
   * `noSleep.enable()` returns a promise when it uses the Screen Wake Lock API, and
   * that request can be denied — Zoom's embedded browser and headless browsers both
   * do. Previously nothing handled the rejection, so a denial surfaced as an
   * unhandled error during drawing submission.
   */
  const enableNoSleep = () => {
    void Promise.resolve()
      .then(() => noSleep.enable())
      .then(() => setIsNoSleepEnabled(true))
      .catch(() => setIsNoSleepEnabled(false));
  };

  const disableNoSleep = () => {
    try {
      noSleep.disable();
    } catch {
      // Already released, or never granted.
    }
    setIsNoSleepEnabled(false);
  };

  useEffect(() => {
    return () => {
      try {
        noSleep.disable();
      } catch {
        // Unmounting; nothing to recover.
      }
    };
  }, [noSleep]);

  return (
    <NoSleepContext.Provider value={{ enableNoSleep, disableNoSleep, isNoSleepEnabled }}>
      {children}
    </NoSleepContext.Provider>
  );
}

export const useNoSleep = (): NoSleepContextProps => {
  const context = useContext(NoSleepContext);
  if (!context) {
    throw new Error('useNoSleep must be used within a NoSleepProvider');
  }
  return context;
};

export default NoSleepProvider;
